import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { Queue } from 'app/models/queue/queue.model';
import { QueueService } from 'app/services/queue/queue.service';
import { tap, catchError } from 'rxjs/operators';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
    GetUserQueue,
    AddToUserQueue,
    UpdateUserQueue,
    DeleteBorrowerFromUserQueue,
    ClearUserQueue
} from 'app/store/queue/queue.actions';
import { PushCallAlert } from 'app/store/call/call.actions';
import { Observable, of, take } from 'rxjs';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { LenderUserState } from '../lender-user/lender-user/lender-user.state';

export class QueueStateModel {
	queue: Queue[];
}

@State<QueueStateModel>({
	name: 'queue',
	defaults: {
		queue: []
	}
})

@Injectable()

export class QueueState {

  @Selector()
	static queue(state: QueueStateModel) {
    return state.queue;
  }

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  lenderUser: LenderUser;

  constructor(
    private queueService: QueueService,
    private pusherService: PusherService,
    private store: Store
  ) { }

  @Action(GetUserQueue , { cancelUncompleted: true })
  getUserQueue({ patchState }: StateContext<QueueStateModel>) {
    return this.queueService.getUserQueue().pipe(catchError(() => {
        this.store.dispatch(new PushCallAlert('error', 'Failed to retrieve your call queue. Please refresh to try again.'));
        return of([]);
      }), tap(queue => {
        if (!_.isArray(queue) && _.isObject(queue)) {
          queue = _.toArray(queue);
        }
        patchState({ queue });
        this.pusherSubscribe();
      })
    );
  }

  @Action(AddToUserQueue)
  addToUserQueue({ getState, patchState }: StateContext<QueueStateModel>, { borrowerId }: AddToUserQueue) {
    const currentQueue = getState().queue;
    const queueCopy = Array.isArray(currentQueue) ? [ ...currentQueue ] : [];
    return this.queueService.addToQueue(borrowerId).pipe(
      catchError(err => {
        this.store.dispatch(new PushCallAlert('error', err.message));
        return of(queueCopy);
      }), tap(queue => {
        if (!_.isArray(queue) && _.isObject(queue)) {
          queue = _.toArray(queue);
        }
        patchState({ queue });
      })
    );
  }

  @Action(UpdateUserQueue)
  updateUserQueue({ getState, patchState }: StateContext<QueueStateModel>, { borrowerIndex, position }: UpdateUserQueue) {
    const queueCopyOne = [ ...getState().queue ];
    const queueCopyTwo = [ ...getState().queue ];
    moveItemInArray(queueCopyTwo, borrowerIndex, position);
    patchState({ queue: queueCopyTwo });
    return this.queueService.updateQueue(queueCopyTwo).pipe(catchError(() => {
        patchState({ queue: queueCopyOne });
        this.store.dispatch(new PushCallAlert('error', 'Failed to save this update to your queue. Please try again.'));
        return of();
      }));
  }

	@Action(DeleteBorrowerFromUserQueue)
	deleteBorrowerFromUserQueue({ getState, patchState }: StateContext<QueueStateModel>, { borrowerId }: DeleteBorrowerFromUserQueue) {
    const queueCopy = [ ...getState().queue ];
		const newQueue = queueCopy.filter(queueCall => queueCall.borrowerId !== borrowerId);
		patchState({ queue: newQueue });
    return this.queueService.deleteQueue(borrowerId, false).pipe(catchError(() => {
        patchState({ queue: queueCopy });
        this.store.dispatch(new PushCallAlert('error', 'Failed to delete borrower from your queue. Please try again.'));
        return of();
      }));
	}

	@Action(ClearUserQueue)
	clearUserQueue({ getState, patchState }: StateContext<QueueStateModel>, { fullClear }: ClearUserQueue) {
    const queueCopy = [ ...getState().queue ];
		patchState({ queue: [] });
		if (!fullClear) {
			return;
		}
    return this.queueService.deleteQueue(null, fullClear).pipe(catchError(() => {
        patchState({ queue: queueCopy });
        this.store.dispatch(new PushCallAlert('error', 'Failed to clear queue. Please try again.'));
        return of();
      }));
	}

  pusherSubscribe() {
    this.lenderUser$.pipe(take(1)).subscribe(currentUser => {
      this.lenderUser = currentUser;
    });

    this.pusherService
      .subscribe(`lender-user-${this.lenderUser.id}-call-queue-channel`)
      .bind_global(this.handlePusherEvent.bind(this));
  }

  handlePusherEvent(eventName: string) {
    if (eventName.startsWith('lender-user-call-queue')) {
      this.store.dispatch(new GetUserQueue());
    }   
  }

  pusherUnsubscribe() {
    this.pusherService.unsubscribe(`lender-user-${this.lenderUser.id}-call-queue-channel`);
  }
}
